/* Presentation-only bridge for the installed domain screens. All DNS store
 * actions, endpoint/payload handling and record editors remain in the package. */
function children(node) {
  if (!node || typeof node !== 'object') return [];
  return node.children || (node.componentOptions && node.componentOptions.children) || [];
}

function markTables(node, h) {
  const nodes = children(node);
  nodes.forEach((child, index) => {
    if (child.tag === 'table') {
      nodes[index] = h('div', {
        class: 'gs-dashboard-domain-table',
        attrs: { tabindex: '0', role: 'region', 'aria-label': 'Domain records table' },
      }, [child]);
    } else {
      markTables(child, h);
    }
  });
}

function classNames(data) {
  if (!data) return '';
  const parts = [];
  if (data.staticClass) parts.push(data.staticClass);
  const value = data.class;
  if (typeof value === 'string') parts.push(value);
  else if (Array.isArray(value)) parts.push(value.filter(item => typeof item === 'string').join(' '));
  else if (value && typeof value === 'object') {
    parts.push(Object.keys(value).filter(key => value[key]).join(' '));
  }
  return parts.join(' ');
}

function hasClass(node, name) {
  return classNames(node && node.data).split(/\s+/).includes(name);
}

function textOf(node) {
  if (node == null) return '';
  if (typeof node !== 'object') return String(node);
  if (node.text) return node.text;
  return children(node).map(textOf).join('');
}

function countClass(node, name, found) {
  const total = found || { n: 0 };
  if (!node || typeof node !== 'object') return total.n;
  if (hasClass(node, name)) total.n += 1;
  children(node).forEach((child) => countClass(child, name, total));
  return total.n;
}

function isEmptyDocument(node) {
  if (!node || typeof node !== 'object') return true;
  if (node.text && String(node.text).trim()) return false;
  const kids = children(node).filter((child) => child && typeof child === 'object');
  if (!kids.length) return true;
  return kids.every(isEmptyDocument);
}

function grooveUser(vm) {
  const state = vm && vm.$store && vm.$store.state;
  if (state && state.user) return state.user;
  if (state && state.core && state.core.user) return state.core.user;
  return null;
}

function appSiteRequestError(vm) {
  if (vm && vm.appSiteRequestError) return vm.appSiteRequestError;
  if (typeof window !== 'undefined' && window.__gdBootstrap && window.__gdBootstrap.appSiteRequestError) {
    return window.__gdBootstrap.appSiteRequestError;
  }
  return null;
}

export function domainAppsNotice(root, { user, requestError } = {}) {
  if (requestError && (requestError.status === 401 || requestError.status >= 500)) {
    return {
      role: 'alert',
      message: requestError.message || (requestError.status === 401
        ? 'Applications could not be loaded. Sign out and try again.'
        : 'Applications could not be loaded. Please try again.'),
    };
  }
  const tiles = countClass(root, 'app-icons-overlay');
  const containers = countClass(root, 'app-icons-container');
  const hasUser = Boolean(user) && user.id != null && String(user.id).trim() !== '';
  if (!hasUser && tiles === 0 && (containers > 0 || isEmptyDocument(root))) {
    return {
      role: 'alert',
      message: 'Your Groove account could not be loaded, so application tiles are unavailable.',
    };
  }
  if (containers > 0 && tiles === 0) {
    return {
      role: 'status',
      message: 'No applications are available for this account.',
    };
  }
  if (isEmptyDocument(root)) {
    return {
      role: 'status',
      message: 'No applications are available for this account.',
    };
  }
  return null;
}

function invokeHandler(handler, event) {
  if (Array.isArray(handler)) handler.forEach(fn => invokeHandler(fn, event));
  else if (typeof handler === 'function') handler(event);
}

/* The installed tile click is on .playWrapper. Give that same control a
   keyboard path without replacing its click handler. */
function armDomainTiles(node) {
  const nodes = children(node);
  nodes.forEach((child) => {
    if (!child || typeof child !== 'object') return;
    if (hasClass(child, 'playWrapper')) {
      const title = nodes.find(sibling => hasClass(sibling, 'app-title'));
      const label = textOf(title).trim() || 'Open application';
      child.data = child.data || {};
      child.data.attrs = {
        ...(child.data.attrs || {}),
        role: 'button',
        tabindex: '0',
        'aria-label': label,
      };
      const on = child.data.on || (child.data.on = {});
      const previous = on.keydown;
      on.keydown = function domainTileKeydown(event) {
        invokeHandler(previous, event);
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        invokeHandler(on.click, event);
      };
    }
    armDomainTiles(child);
  });
}

export function createDashboardDomainsAdapter(LegacyAccountDomains) {
  if (!LegacyAccountDomains || !LegacyAccountDomains.components) return LegacyAccountDomains;
  const LegacyCustomDomains = LegacyAccountDomains.components.CustomDomains;
  if (!LegacyCustomDomains || !LegacyCustomDomains.components || !LegacyCustomDomains.components.ZoneRecords) return LegacyAccountDomains;
  const LegacyZoneRecords = LegacyCustomDomains.components.ZoneRecords;
  const ZoneRecords = {
    extends: LegacyZoneRecords,
    render(h) {
      const root = LegacyZoneRecords.render.call(this, h);
      root.data = { ...root.data, class: [root.data && root.data.class, 'gs-dashboard-dns-records'] };
      markTables(root, h);
      root.children.unshift(h('h3', { class: 'gs-dashboard-dns-heading' }, 'DNS records'));
      if (!this.editableRecords.length) {
        root.children.push(h('p', { attrs: { role: 'status' }, class: 'gs-dashboard-dns-message' },
          'There are no editable DNS records for this domain.'));
      }
      return root;
    },
  };
  const CustomDomains = {
    extends: LegacyCustomDomains,
    components: { ...LegacyCustomDomains.components, ZoneRecords },
    data() {
      return {
        dashboardDnsError: false,
        dashboardDnsTimedOut: false,
        dashboardDnsDomainId: null,
        dashboardDnsLoading: false,
        dashboardDnsTimeoutMs: 15000,
        dashboardDnsToken: 0,
      };
    },
    watch: {
      selectedPanel() {
        this.dashboardDnsError = false;
        this.dashboardDnsTimedOut = false;
        this.$nextTick(() => {
          const body = this.$el.closest('.gs-modal__body');
          if (body) { body.scrollLeft = 0; body.scrollTop = 0; }
          const back = this.$el.querySelector('button');
          if (back) back.focus({ preventScroll: true });
        });
      },
    },
    methods: {
      async fetchDnsRecords(id) {
        this.dashboardDnsDomainId = id;
        this.dashboardDnsError = false;
        this.dashboardDnsTimedOut = false;
        this.dashboardDnsLoading = true;
        // The installed method owns the request and only opens the editor
        // after a successful read. A hung read used to leave the button idle.
        this.showLoader = true;
        const token = (this.dashboardDnsToken = (this.dashboardDnsToken || 0) + 1);
        const timeoutMs = this.dashboardDnsTimeoutMs || 15000;
        let timer;
        let legacyCall;
        try {
          legacyCall = LegacyCustomDomains.methods.fetchDnsRecords.call(this, id);
        } catch (error) {
          legacyCall = Promise.reject(error);
        }
        const timeout = new Promise((resolve) => {
          timer = setTimeout(() => resolve('timeout'), timeoutMs);
        });
        try {
          const winner = await Promise.race([
            Promise.resolve(legacyCall).then(() => 'done', () => 'done'),
            timeout,
          ]);
          if (token !== this.dashboardDnsToken) return;
          const opened = this.selectedPanel === 'manage_records';
          if (winner === 'timeout' && !opened) {
            this.dashboardDnsTimedOut = true;
            this.dashboardDnsError = true;
            this.showLoader = false;
          } else {
            this.dashboardDnsError = !opened;
          }
        } finally {
          clearTimeout(timer);
          if (token === this.dashboardDnsToken) this.dashboardDnsLoading = false;
        }
      },
    },
    render(h) {
      const root = LegacyCustomDomains.render.call(this, h);
      root.data = { ...root.data, class: [root.data && root.data.class, 'gs-dashboard-custom-domains'] };
      root.children = root.children || [];
      markTables(root, h);
      armDomainTiles(root);
      const notice = domainAppsNotice(root, {
        user: grooveUser(this),
        requestError: appSiteRequestError(this),
      });
      if (notice) {
        root.children.unshift(h('p', {
          class: 'gs-dashboard-apps-message',
          attrs: { role: notice.role },
        }, notice.message));
      }
      if (this.dashboardDnsLoading && this.selectedPanel !== 'manage_records') {
        root.children.unshift(h('p', {
          attrs: { role: 'status' },
          class: 'gs-dashboard-dns-message',
        }, 'Loading DNS records…'));
      }
      if (this.dashboardDnsError) {
        const detail = this.dashboardDnsTimedOut
          ? 'DNS records could not be loaded because the request timed out. Your domain settings have not changed.'
          : 'DNS records could not be loaded. Your domain settings have not changed.';
        root.children.unshift(h('div', { class: 'gs-dashboard-dns-message', attrs: { role: 'alert' } }, [
          h('p', detail),
          h('button', {
            class: 'gs-btn gs-btn--outline gs-btn--sm',
            attrs: { type: 'button', disabled: this.showLoader || this.dashboardDnsLoading },
            on: { click: () => this.fetchDnsRecords(this.dashboardDnsDomainId) },
          }, 'Retry loading DNS records'),
        ]));
      }
      return root;
    },
  };
  return {
    extends: LegacyAccountDomains,
    components: { ...LegacyAccountDomains.components, CustomDomains },
  };
}
